import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {NgClass, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {AboutWhatsappComponent} from '../about-whatsapp/about-whatsapp.component';
import {
  DisconnectStep,
  DisconnectWhatsappComponent,
} from '../disconnect-whatsapp/disconnect-whatsapp.component';
import {WhatsappService} from '../../../services/whatsapp.service';

/** Which panel of the connect flow is currently on screen. */
type WhatsappStep = 'overview' | 'enterNumber' | 'confirmCode';

/** Where "Contact support" on the disconnect sheet points. */
const SUPPORT_EMAIL = 'support@bellabeat.com';

@Component({
  selector: 'app-connect-whatsapp',
  standalone: true,
  imports: [NgIf, NgClass, FormsModule, AboutWhatsappComponent, DisconnectWhatsappComponent],
  templateUrl: './connect-whatsapp.component.html'
})
export class ConnectWhatsappComponent implements OnInit {

  /** Whether the "Chat with IVY on WhatsApp" notifications toggle is on. */
  whatsappEnabled = false;

  /** Whether the "About WhatsApp Integration" article is open. */
  showAbout = false;

  /** Current step of the connect/confirm flow. */
  step: WhatsappStep = 'overview';

  /** Sheet of the disconnect flow currently open, or `null` when it is closed. */
  disconnectStep: DisconnectStep | null = null;

  /** Error shown inside the disconnect sheet (kept apart from the page-level one). */
  disconnectError = '';

  /** Bound to the phone-number input (E.164, e.g. +385911234567). */
  phoneNumber = '';

  /** Bound to the confirmation-code input. */
  confirmationCode = '';

  /** Number awaiting confirmation (kept while the code panel is open). */
  private pendingNumber = '';

  /** In-flight request flag, disables the action buttons. */
  loading = false;

  /** User-facing error and status messages. */
  errorMessage = '';
  infoMessage = '';

  /** Emits when the article opens/closes so the container can go full-width. */
  @Output() aboutOpenChange = new EventEmitter<boolean>();

  constructor(protected whatsapp: WhatsappService) {}

  ngOnInit(): void {
    this.whatsappEnabled = this.whatsapp.isConnected;
  }

  get connectedNumber(): string | null {
    return this.whatsapp.connectedNumber;
  }

  get isConnected(): boolean {
    return this.whatsapp.isConnected;
  }

  // --- Overview actions ------------------------------------------------------

  /** Primary button: start connecting or manage the existing number. */
  onPrimaryAction(): void {
    this.resetMessages();
    if (this.isConnected) {
      // Already connected: offer to disconnect the current number.
      this.openDisconnectFlow();
      return;
    }
    this.phoneNumber = '';
    this.step = 'enterNumber';
  }

  toggleWhatsapp(): void {
    if (!this.isConnected) {
      return;
    }
    if (this.whatsappEnabled) {
      // Turning the switch off means disconnecting; the switch only flips once
      // the user confirms, so cancelling leaves it on.
      this.openDisconnectFlow();
      return;
    }
    this.whatsappEnabled = true;
    // TODO: persist the notification preference once a backend endpoint exists.
  }

  // --- Enter number ----------------------------------------------------------

  submitNumber(): void {
    this.resetMessages();
    const number = this.phoneNumber.trim();
    if (!this.isValidNumber(number)) {
      this.errorMessage = 'Enter a valid phone number in international format, e.g. +385911234567.';
      return;
    }

    this.loading = true;
    this.whatsapp.registerNumber(number).subscribe({
      next: () => {
        this.loading = false;
        this.pendingNumber = number;
        this.confirmationCode = '';
        this.infoMessage = `We sent a confirmation code to ${number} on WhatsApp.`;
        this.step = 'confirmCode';
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = this.readError(err, 'Could not register that number. Please try again.');
      },
    });
  }

  // --- Confirm code ----------------------------------------------------------

  submitCode(): void {
    this.resetMessages();
    const code = this.confirmationCode.trim();
    if (!code) {
      this.errorMessage = 'Enter the confirmation code you received.';
      return;
    }

    this.loading = true;
    this.whatsapp.confirmNumber(code).subscribe({
      next: () => {
        this.loading = false;
        this.whatsapp.setConnectedNumber(this.pendingNumber);
        this.whatsappEnabled = true;
        this.pendingNumber = '';
        this.step = 'overview';
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = this.readError(err, 'That code did not work. Check it and try again.');
      },
    });
  }

  resendCode(): void {
    this.resetMessages();
    this.loading = true;
    this.whatsapp.resendConfirmationCode().subscribe({
      next: () => {
        this.loading = false;
        this.infoMessage = 'A new confirmation code is on its way.';
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = this.readError(err, 'Could not resend the code. Please try again.');
      },
    });
  }

  // --- Disconnect ------------------------------------------------------------

  private openDisconnectFlow(): void {
    this.disconnectError = '';
    this.disconnectStep = 'confirm';
  }

  /** DISCONNECT on the confirm sheet: drop the number, then ask why. */
  onDisconnectConfirm(): void {
    this.loading = true;
    this.disconnectError = '';
    this.whatsapp.deleteNumber().subscribe({
      next: () => {
        this.loading = false;
        this.whatsapp.setConnectedNumber(null);
        this.whatsappEnabled = false;
        this.disconnectStep = 'feedback';
      },
      error: (err) => {
        this.loading = false;
        this.disconnectError = this.readError(err, 'Could not disconnect the number. Please try again.');
      },
    });
  }

  /** CANCEL, backdrop tap or Escape on either sheet. */
  onDisconnectCancel(): void {
    this.disconnectStep = null;
    this.disconnectError = '';
  }

  onDisconnectFeedback(reason: string): void {
    // TODO: send the reason once the backend exposes a feedback endpoint.
    console.debug('WhatsApp disconnect reason:', reason);
    this.onDisconnectCancel();
  }

  onContactSupport(): void {
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('WhatsApp integration')}`;
  }

  // --- Navigation ------------------------------------------------------------

  cancelFlow(): void {
    this.resetMessages();
    this.pendingNumber = '';
    this.confirmationCode = '';
    this.step = 'overview';
  }

  openAbout(): void {
    this.showAbout = true;
    this.aboutOpenChange.emit(true);
  }

  closeAbout(): void {
    this.showAbout = false;
    this.aboutOpenChange.emit(false);
  }

  // --- Helpers ---------------------------------------------------------------

  private isValidNumber(number: string): boolean {
    // E.164: leading +, first digit 1-9, up to 15 digits total.
    return /^\+[1-9]\d{6,14}$/.test(number);
  }

  private resetMessages(): void {
    this.errorMessage = '';
    this.infoMessage = '';
  }

  private readError(err: any, fallback: string): string {
    return err?.error?.message || err?.error?.msg || fallback;
  }
}
